// T-335 — immagine con pinch-zoom + pan + doppio-tap, senza dipendenze esterne
// (nessun swiper/zoom lib nel progetto). Pointer Events → funziona sia nella
// WebView Capacitor (Android/iOS) sia nella PWA. Usata nel viewer fullscreen
// dei documenti caricati ("apri in full screen per zoomare").

import { useRef, useState } from 'react';

interface Transform {
  scale: number;
  x: number;
  y: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_SCALE = 2.5;

export function ZoomableImage({ src, alt }: { src: string; alt?: string }): JSX.Element {
  const [t, setT] = useState<Transform>({ scale: 1, x: 0, y: 0 });
  const tRef = useRef<Transform>({ scale: 1, x: 0, y: 0 });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchStart = useRef<{ dist: number; scale: number } | null>(null);
  const panStart = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const lastTap = useRef(0);
  const [animate, setAnimate] = useState(false);

  const apply = (next: Transform): void => {
    tRef.current = next;
    setT(next);
  };

  const onDown = (e: React.PointerEvent): void => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    setAnimate(false);
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchStart.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: tRef.current.scale };
      panStart.current = null;
    } else if (pointers.current.size === 1) {
      panStart.current = { x: e.clientX, y: e.clientY, tx: tRef.current.x, ty: tRef.current.y };
      const now = Date.now();
      if (now - lastTap.current < 300) {
        setAnimate(true);
        apply(tRef.current.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: DOUBLE_TAP_SCALE, x: 0, y: 0 });
      }
      lastTap.current = now;
    }
  };

  const onMove = (e: React.PointerEvent): void => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, pinchStart.current.scale * (dist / pinchStart.current.dist)));
      apply({ scale, x: tRef.current.x, y: tRef.current.y });
    } else if (pointers.current.size === 1 && panStart.current && tRef.current.scale > 1) {
      apply({
        scale: tRef.current.scale,
        x: panStart.current.tx + (e.clientX - panStart.current.x),
        y: panStart.current.ty + (e.clientY - panStart.current.y),
      });
    }
  };

  const onUp = (e: React.PointerEvent): void => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) {
      panStart.current = null;
      if (tRef.current.scale <= 1) {
        setAnimate(true);
        apply({ scale: 1, x: 0, y: 0 });
      }
    }
  };

  return (
    <div
      style={{
        width: '100%', height: '100%', overflow: 'hidden', touchAction: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000',
      }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          maxWidth: '100%', maxHeight: '100%',
          transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
          transformOrigin: 'center center',
          transition: animate ? 'transform 0.18s ease-out' : 'none',
          userSelect: 'none', WebkitUserSelect: 'none',
        }}
      />
    </div>
  );
}
